import { useState, useEffect } from 'react'
import { ASSET_CATEGORY_FOR_TYPE } from '@deepguide-ai/types'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  createFileRoute,
  useNavigate,
  useParams,
  useSearch,
} from '@tanstack/react-router'
import AddAssetDialog from '@/pages/assets/AddAssetDialog'
import RegisterAiSystemDialog, {
  type RegisterAiSystemPrefill,
} from '@/pages/assets/RegisterAiSystemDialog'
import { AiRegistrationPrompt } from '@/pages/assets/AiRegistrationPrompt'
import { AiSystemCandidatesCard } from '@/pages/assets/AiSystemCandidatesCard'
import { AssetSyncDialog } from '@/pages/assets/AssetSyncDialog'
import { AssetScopeDialog } from '@/pages/assets/AssetScopeDialog'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'
import {
  ASSET_CATEGORY_LABELS,
  ASSET_TYPE_GROUPS,
  ASSET_TYPE_LABELS,
  CLASSIFICATION_LABELS,
  CRITICALITY_LABELS,
  classificationColor,
  criticalityColor,
  isSyncedAsset,
  type AssetTypeFilter,
  type CriticalityFilter,
  type ClassificationFilter,
  type AssetEphiFilter,
  type ScopeFilter,
} from '@/pages/assets/type'
import { FilterPill } from '@/components/ui/filter-pill'
import { useSetAtom } from 'jotai'
import {
  Plus,
  Boxes,
  AlertTriangle,
  RefreshCw,
  Eye,
  Download,
} from 'lucide-react'
import { toast } from 'sonner'
import { screenataPageContextAtom } from '@/stores/screenataPageContextStore'
import { formatEnrolledFrameworksLabel } from '@/lib/compliance-framework'
import { formatDate } from '@/lib/format'
import { downloadBase64File } from '@/utils/download'
import { paginationSearchSchema } from '@/lib/pagination'
import { trpc } from '@/lib/trpc'
import { ClassificationSuggestionCard } from '@/pages/assets/ClassificationSuggestion'
import {
  useEnabledFrameworks,
  useHipaaEnabled,
} from '@/hooks/useComplianceFramework'
import { useWorkspacePermissions } from '@/hooks/useWorkspacePermissions'
import { useUrlPagination } from '@/hooks/useUrlPagination'
import { TablePaginationFooter } from '@/components/ui/TablePaginationFooter'
import { Button } from '@deepguide-ai/ui/button'
import { Checkbox } from '@deepguide-ai/ui/checkbox'
import { SearchableSelect } from '@deepguide-ai/ui/combobox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@deepguide-ai/ui/select'
import { StatCardSkeleton } from '@deepguide-ai/ui/stat-card-skeleton'
import {
  PageEmptyState,
  pageEmptyStatePrimaryButtonClass,
} from '@deepguide-ai/ui/page-empty-state'
import { TableEmptyState } from '@deepguide-ai/ui/table-empty-state'
import { TableSkeleton } from '@deepguide-ai/ui/table-skeleton'
import ScreenataPageHeader from '@/components/compliance/ScreenataPageHeader'
import { PageHeaderActions } from '@/components/layout/PageHeaderActions'
import { AssignAccessBulkButton } from '@/components/compliance/AssetPeopleActions'

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/assets'
)({
  component: AssetInventoryPage,
  validateSearch: paginationSearchSchema,
  staticData: { pageHeader: { title: 'Assets' } },
})

function AssetInventoryPage() {
  const navigate = useNavigate()
  const { workspaceId } = useParams({ strict: false }) as {
    workspaceId: string
  }
  const { canWorkspaceWrite, isAdmin } = useWorkspacePermissions(workspaceId)
  const enrolledFrameworks = useEnabledFrameworks()
  const enrolledLabel = formatEnrolledFrameworksLabel(enrolledFrameworks)
  const hipaaEnabled = useHipaaEnabled()
  const queryClient = useQueryClient()

  const [typeFilter, setTypeFilter] = useState<AssetTypeFilter>('all')
  const [criticalityFilter, setCriticalityFilter] =
    useState<CriticalityFilter>('all')
  const [classificationFilter, setClassificationFilter] =
    useState<ClassificationFilter>('all')
  const [ephiFilter, setEphiFilter] = useState<AssetEphiFilter>('all')
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>('in_scope')
  const [categoryFilter, setCategoryFilter] = useState<
    'all' | 'devices' | 'software' | 'cloud'
  >('all')
  const [showAddForm, setShowAddForm] = useState(false)
  const [showSyncDialog, setShowSyncDialog] = useState(false)
  // RFC-231 M1 — non-null while the AI-system registration form is open; the
  // value is the prefill (provider from onboarding / a discovery candidate).
  const [registerAi, setRegisterAi] = useState<RegisterAiSystemPrefill | null>(
    null
  )
  // RFC-231 dark launch — the whole AI-governance surface (tile, register
  // button, prompt, candidates, dialog) hides until the flag is on for this
  // workspace. The server gates its procedures on the same flag.
  const { enabled: aiGovernanceEnabled } = useFeatureFlag('ai_governance')
  // Both routes into "I'm looking at AI systems": the register bucket
  // (category) and the AI Systems stat card, which sets the TYPE filter.
  const viewingAiSystems =
    categoryFilter === 'ai_system' || typeFilter === 'AI_SYSTEM'
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [scopeDialog, setScopeDialog] = useState<{
    ids: string[]
    action: 'out_of_scope' | 'in_scope'
  } | null>(null)
  const [selectedSyncTargets, setSelectedSyncTargets] = useState<string[]>([])
  const [isSyncingSources, setIsSyncingSources] = useState(false)
  const search = useSearch({
    from: '/(protected)/workspaces/$workspaceId/assets',
  })
  const { page, pageSize, setPage, setPageSize } = useUrlPagination(search, [
    typeFilter,
    criticalityFilter,
    classificationFilter,
    ephiFilter,
    scopeFilter,
    categoryFilter,
  ])

  // Queries
  const statsQuery = useQuery(trpc.assetManagement.getStats.queryOptions())
  const syncStatusQuery = useQuery(
    trpc.assetManagement.getSyncStatus.queryOptions()
  )
  const syncSourcesQuery = useQuery(
    trpc.assetManagement.listSyncSources.queryOptions()
  )
  const listQuery = useQuery({
    ...trpc.assetManagement.list.queryOptions({
      page,
      pageSize,
      ...(typeFilter !== 'all' ? { assetType: typeFilter as any } : {}),
      ...(criticalityFilter !== 'all'
        ? { criticality: criticalityFilter as any }
        : {}),
      ...(classificationFilter !== 'all'
        ? { dataClassification: classificationFilter as any }
        : {}),
      ...(hipaaEnabled && ephiFilter === 'ephi_only'
        ? { containsEphi: true }
        : {}),
      ...(hipaaEnabled && ephiFilter === 'phi_free'
        ? { containsEphi: false }
        : {}),
      ...(scopeFilter !== 'all' ? { scope: scopeFilter } : {}),
      ...(categoryFilter !== 'all' ? { category: categoryFilter } : {}),
    }),
    placeholderData: (previousData) => previousData,
  })

  const rolesQuery = useQuery(trpc.complianceRole.list.queryOptions())

  const syncMutation = useMutation(
    trpc.assetManagement.syncNow.mutationOptions()
  )

  const bulkUpdateMutation = useMutation(
    trpc.assetManagement.bulkUpdate.mutationOptions()
  )

  const exportMutation = useMutation({
    ...trpc.assetManagement.exportAssetList.mutationOptions(),
    onSuccess: (data) => {
      downloadBase64File(data.data, data.filename, data.contentType)
      toast.success('Asset inventory exported')
    },
    onError: () => toast.error('Failed to export asset inventory'),
  })

  const stats = statsQuery.data
  const pageItems = listQuery.data?.items ?? []
  const totalItems = listQuery.data?.total ?? 0
  // Bulk edit is admin-only server-side (assetManagement.bulkUpdate), so the
  // selection UI is gated on isAdmin — not canWorkspaceWrite.
  const hasSelection = isAdmin && selectedIds.size > 0
  const providerGroups = syncSourcesQuery.data ?? []
  const isListInitialLoading = listQuery.isLoading && !listQuery.data
  const isEmpty =
    totalItems === 0 &&
    typeFilter === 'all' &&
    criticalityFilter === 'all' &&
    classificationFilter === 'all' &&
    ephiFilter === 'all' &&
    (scopeFilter === 'all' ||
      (scopeFilter === 'in_scope' && stats?.total === 0)) &&
    categoryFilter === 'all'

  const invalidateAll = () => {
    queryClient.invalidateQueries({
      queryKey: trpc.assetManagement.list.queryKey(),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.assetManagement.getStats.queryKey(),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.assetManagement.getSyncStatus.queryKey(),
    })
  }

  useEffect(() => {
    if (!isAdmin && selectedIds.size > 0) {
      setSelectedIds(new Set())
    }
  }, [isAdmin, selectedIds.size])

  const handleBulkUpdate = async (data: Record<string, unknown>) => {
    if (!isAdmin || selectedIds.size === 0) return
    try {
      await bulkUpdateMutation.mutateAsync({
        ids: [...selectedIds],
        data: data as any,
      })
      invalidateAll()
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to update assets')
    }
  }

  const toggleSelectAll = () => {
    if (!isAdmin) return
    const pageIds = pageItems.map((i: any) => i.id)
    const allOnPageSelected =
      pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id))
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (allOnPageSelected) {
        for (const id of pageIds) next.delete(id)
      } else {
        for (const id of pageIds) next.add(id)
      }
      return next
    })
  }

  const toggleSelect = (id: string) => {
    if (!isAdmin) return
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  useEffect(() => {
    if (!showSyncDialog) return
    const availableKeys = providerGroups.flatMap((group: any) =>
      group.targets.map((target: any) => target.key)
    )
    setSelectedSyncTargets((current) => {
      const stillAvailable = current.filter((key) =>
        availableKeys.includes(key)
      )
      return stillAvailable.length > 0 ? stillAvailable : availableKeys
    })
  }, [showSyncDialog, providerGroups])

  const handleToggleSyncTarget = (key: string, checked: boolean) => {
    setSelectedSyncTargets((current) => {
      if (checked) {
        return current.includes(key) ? current : [...current, key]
      }
      return current.filter((value) => value !== key)
    })
  }

  const handleToggleSyncProvider = (providerKey: string, checked: boolean) => {
    const providerGroup = providerGroups.find(
      (group: any) => group.providerKey === providerKey
    )
    if (!providerGroup) return

    const targetKeys = providerGroup.targets.map((target: any) => target.key)
    setSelectedSyncTargets((current) => {
      if (checked) {
        return [...new Set([...current, ...targetKeys])]
      }
      return current.filter((key) => !targetKeys.includes(key))
    })
  }

  const handleSyncSelectedSources = async () => {
    if (!canWorkspaceWrite) return
    const selectedProviders = providerGroups
      .map((group: any) => ({
        key: group.providerKey,
        label: group.providerLabel,
        targetKeys: group.targets
          .filter((target: any) => selectedSyncTargets.includes(target.key))
          .map((target: any) => target.key),
      }))
      .filter((group: any) => group.targetKeys.length > 0)

    if (selectedProviders.length === 0) {
      toast.error('Select at least one source to sync')
      return
    }

    setIsSyncingSources(true)

    const successes: string[] = []
    const failures: string[] = []

    for (const source of selectedProviders) {
      try {
        const result = await syncMutation.mutateAsync({
          provider: source.key,
          targetKeys: source.targetKeys,
        } as any)
        const parts = [
          `${result.created} created`,
          `${result.updated} updated`,
          result.orphaned > 0 ? `${result.orphaned} orphaned` : null,
        ].filter(Boolean)
        if (result.errors > 0) {
          failures.push(`${source.label}: ${parts.join(', ')}; ${result.errors} collection errors`)
        } else {
          successes.push(`${source.label}: ${parts.join(', ')}`)
        }
      } catch (err: any) {
        failures.push(`${source.label}: ${err?.message ?? 'Sync failed'}`)
      }
    }

    invalidateAll()
    setIsSyncingSources(false)

    if (successes.length > 0) {
      toast.success('Assets synced', {
        description: successes.join(' | '),
      })
    }

    if (failures.length > 0) {
      toast.error(
        failures.length === selectedProviders.length
          ? 'Asset sync failed'
          : 'Some asset sources failed to sync',
        {
          description: failures.join(' | '),
        }
      )
    }

    if (failures.length === 0) {
      setShowSyncDialog(false)
    }
  }

  const openAssetDetail = (assetId: string) => {
    navigate({
      to: '/workspaces/$workspaceId/assets/$assetId',
      params: { workspaceId, assetId },
    })
  }

  // Page context for workspace chat
  const setPageContext = useSetAtom(screenataPageContextAtom)
  useEffect(() => {
    setPageContext({
      page: 'assets',
      summary: [
        `Asset Inventory: ${stats?.total ?? 0} assets.`,
        `${stats?.critical ?? 0} critical, ${stats?.unclassified ?? 0} unclassified, ${stats?.needsReview ?? 0} needing review.`,
        hipaaEnabled ? `${stats?.ephiCount ?? 0} contain ePHI.` : '',
        `Showing ${totalItems} items with current filters.`,
      ]
        .filter(Boolean)
        .join(' '),
      data: stats ?? {},
    })
    return () => setPageContext(null)
  }, [stats, totalItems, setPageContext, hipaaEnabled])

  return (
    <div className='flex flex-col gap-4'>
      <PageHeaderActions>
        <Button
          size='sm'
          variant='outline'
          className='h-8 rounded-sm border-border-divider px-3 text-[12px] text-text-body'
          onClick={() => exportMutation.mutate()}
          disabled={exportMutation.isPending}
        >
          <Download className=' h-3.5 w-3.5' />
          {exportMutation.isPending ? 'Exporting…' : 'Export'}
        </Button>
        {canWorkspaceWrite && (
          <>
            <Button
              size='sm'
              variant='outline'
              className='h-8 rounded-sm border-border-divider px-3 text-[12px] text-text-body'
              onClick={() => setShowSyncDialog(true)}
              disabled={isSyncingSources}
            >
              <RefreshCw
                className={` h-3.5 w-3.5 ${isSyncingSources ? 'animate-spin' : ''}`}
              />
              {isSyncingSources ? 'Syncing…' : 'Sync Assets'}
            </Button>
            {aiGovernanceEnabled && (
              <Button
                size='sm'
                variant='outline'
                className='h-8 rounded-sm border-border-divider px-3 text-[12px] text-text-body'
                onClick={() => setRegisterAi({})}
              >
                <Plus className='h-3.5 w-3.5' />
                Register AI system
              </Button>
            )}
            <Button
              size='sm'
              className='h-8 rounded-sm bg-primary px-3 text-[12px] text-primary-foreground hover:bg-primary/90'
              onClick={() => setShowAddForm(true)}
            >
              <Plus className='h-3.5 w-3.5' />
              Add Asset
            </Button>
          </>
        )}
      </PageHeaderActions>
      {/* Page title */}
      <ScreenataPageHeader
        title='Asset Inventory'
        description={`Track infrastructure assets, data classification, criticality, and ownership for ${enrolledLabel} audits.${syncStatusQuery.data?.lastSyncedAt ? ` Last synced ${formatDate(syncStatusQuery.data.lastSyncedAt)}.` : ''}`}
      />

      {/* Stat cards */}
      <div
        className={`grid grid-cols-1 gap-3 sm:grid-cols-2 ${hipaaEnabled ? 'xl:grid-cols-6' : 'xl:grid-cols-5'}`}
      >
        {statsQuery.isLoading && !stats ? (
          Array.from({ length: hipaaEnabled ? 6 : 5 }, (_, index) => (
            <StatCardSkeleton key={index} className='min-h-[90px]' />
          ))
        ) : (
          <>
            <div className='rounded-sm border border-border-surface bg-card px-5 py-4'>
              <div className='font-body text-[10px] font-semibold tracking-[1px] text-text-subtle uppercase'>
                Total Assets
              </div>
              <div className='font-heading mt-2 text-[22px] font-bold text-foreground'>
                {stats?.total ?? 0}
              </div>
            </div>
            <div className='rounded-sm border border-border-surface bg-card px-5 py-4'>
              <div className='font-body text-[10px] font-semibold tracking-[1px] text-text-subtle uppercase'>
                Critical
              </div>
              <div className='font-heading mt-2 text-[22px] font-bold text-status-red'>
                {stats?.critical ?? 0}
              </div>
            </div>
            <div className='rounded-sm border border-border-surface bg-card px-5 py-4'>
              <div className='font-body text-[10px] font-semibold tracking-[1px] text-text-subtle uppercase'>
                Unclassified
              </div>
              <div className='font-heading mt-2 text-[22px] font-bold text-status-orange'>
                {stats?.unclassified ?? 0}
              </div>
            </div>
            <div className='rounded-sm border border-border-surface bg-card px-5 py-4'>
              <div className='font-body text-[10px] font-semibold tracking-[1px] text-text-subtle uppercase'>
                Needs Review
              </div>
              <div className='font-heading mt-2 text-[22px] font-bold text-status-orange'>
                {stats?.needsReview ?? 0}
              </div>
            </div>
            {/* RFC-216 M1 / RFC-231 — the AI inventory tile sits in the row like
                every other count. Clicking filters the table to AI systems; the
                header's "Register AI system" is the always-on entry. */}
            {aiGovernanceEnabled && (
              <button
                type='button'
                onClick={() => {
                  setCategoryFilter('all')
                  setTypeFilter('AI_SYSTEM' as AssetTypeFilter)
                }}
                className='rounded-sm border border-border-surface bg-card px-5 py-4 text-left transition-colors hover:border-ring'
              >
                <div className='font-body text-[10px] font-semibold tracking-[1px] text-text-subtle uppercase'>
                  AI Systems
                </div>
                <div className='font-heading mt-2 text-[22px] font-bold text-foreground'>
                  {stats?.aiSystemCount ?? 0}
                </div>
              </button>
            )}
            {hipaaEnabled && (
              <button
                type='button'
                onClick={() => setEphiFilter('ephi_only')}
                className='rounded-sm border border-border-surface bg-card px-5 py-4 text-left transition-colors hover:border-ring'
              >
                <div className='font-body text-[10px] font-semibold tracking-[1px] text-text-subtle uppercase'>
                  ePHI Assets
                </div>
                <div className='font-heading mt-2 text-[22px] font-bold text-status-red'>
                  {stats?.ephiCount ?? 0}
                </div>
              </button>
            )}
          </>
        )}
      </div>

      {/* Filter row */}
      <div className='flex flex-wrap items-center gap-3'>
        {/* One grouped Type filter: register buckets (Eben 2026-07-16) as
            selectable "All <bucket>" items, individual types beneath. */}
        <SearchableSelect
          value={
            typeFilter !== 'all'
              ? typeFilter
              : categoryFilter !== 'all'
                ? `cat:${categoryFilter}`
                : 'all'
          }
          onValueChange={(v) => {
            if (v.startsWith('cat:')) {
              setCategoryFilter(v.slice(4) as typeof categoryFilter)
              setTypeFilter('all')
            } else {
              setCategoryFilter('all')
              setTypeFilter(v as AssetTypeFilter)
            }
          }}
          options={[
            {
              value: 'all',
              label: `All Types${stats ? ` (${stats.total})` : ''}`,
            },
            ...ASSET_TYPE_GROUPS.flatMap((group) => {
              const bucketCount = {
                devices: stats?.devicesCount,
                software: stats?.softwareCount,
                cloud: stats?.cloudCount,
                other: undefined,
              }[group.category]
              return [
                ...(group.category !== 'other'
                  ? [
                      {
                        value: `cat:${group.category}`,
                        label: `All ${group.label}${bucketCount != null ? ` (${bucketCount})` : ''}`,
                        group: group.label,
                      },
                    ]
                  : []),
                ...group.types.map((type) => ({
                  value: type,
                  label: ASSET_TYPE_LABELS[type],
                  group: group.label,
                })),
              ]
            }),
          ]}
          placeholder='Type'
          searchPlaceholder='Search asset types...'
          emptyMessage='No asset types found.'
          triggerProps={{
            className:
              'font-body h-8 w-[180px] rounded-sm border border-border-divider bg-card px-3 text-[12px] font-medium text-muted-foreground',
          }}
          contentProps={{
            popoverOptions: { style: { width: '240px' } },
          }}
        />

        <Select
          value={criticalityFilter}
          onValueChange={(v) => setCriticalityFilter(v as CriticalityFilter)}
        >
          <SelectTrigger className='font-body h-8 w-[140px] rounded-sm border border-border-divider bg-card px-3 text-[12px] font-medium text-muted-foreground'>
            <SelectValue placeholder='Criticality' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>All Criticality</SelectItem>
            {Object.entries(CRITICALITY_LABELS).map(([k, v]) => (
              <SelectItem key={k} value={k}>
                {v}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={classificationFilter}
          onValueChange={(v) =>
            setClassificationFilter(v as ClassificationFilter)
          }
        >
          <SelectTrigger className='font-body h-8 w-[160px] rounded-sm border border-border-divider bg-card px-3 text-[12px] font-medium text-muted-foreground'>
            <SelectValue placeholder='Classification' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>All Classifications</SelectItem>
            {Object.entries(CLASSIFICATION_LABELS).map(([k, v]) => (
              <SelectItem key={k} value={k}>
                {v}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={scopeFilter}
          onValueChange={(v) => setScopeFilter(v as ScopeFilter)}
        >
          <SelectTrigger className='font-body h-8 w-[140px] rounded-sm border border-border-divider bg-card px-3 text-[12px] font-medium text-muted-foreground'>
            <SelectValue placeholder='Scope' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>All Scope</SelectItem>
            <SelectItem value='in_scope'>In scope</SelectItem>
            <SelectItem value='out_of_scope'>Out of scope</SelectItem>
          </SelectContent>
        </Select>

        {hipaaEnabled && (
          <>
            <div className='mx-1 h-5 w-px bg-border-soft' />
            <span className='font-body text-[12px] text-muted-foreground'>ePHI:</span>
            <FilterPill
              active={ephiFilter === 'all'}
              onClick={() => setEphiFilter('all')}
            >
              All
            </FilterPill>
            <FilterPill
              active={ephiFilter === 'ephi_only'}
              lightActiveClassName='bg-status-red text-white'
              onClick={() => setEphiFilter('ephi_only')}
            >
              Contains ePHI
            </FilterPill>
            <FilterPill
              active={ephiFilter === 'phi_free'}
              lightActiveClassName='bg-status-green text-white'
              onClick={() => setEphiFilter('phi_free')}
            >
              PHI-free
            </FilterPill>
          </>
        )}
      </div>

      {(categoryFilter === 'devices' ||
        typeFilter === 'DEVICE' ||
        typeFilter === 'FACILITY') && (
        <p className='font-body -mt-1 text-[12px] text-muted-foreground'>
          Employee laptops and phones (MDM / BYOD) are tracked under{' '}
          <a
            href={`/workspaces/${workspaceId}/people`}
            className='text-cobalt-strong underline-offset-2 hover:underline'
          >
            People → Devices
          </a>
          ; this bucket holds company equipment registered as inventory assets.
        </p>
      )}

      {/* RFC-231 M1 / D6 — the registration prompt fires from the onboarding
          answer alone and disappears once one AI system exists. Shown on the
          unfiltered view and the AI Systems bucket, where someone looking for
          their AI would land. */}
      {aiGovernanceEnabled &&
        canWorkspaceWrite &&
        (viewingAiSystems ||
          (typeFilter === 'all' && categoryFilter === 'all')) && (
          <>
            <AiRegistrationPrompt onRegister={(p) => setRegisterAi(p ?? {})} />
            {/* RFC-231 M2 — prefill candidates, only where the user is looking
                at AI systems; the unfiltered view keeps just the prompt. */}
            {viewingAiSystems && (
              <AiSystemCandidatesCard onRegister={(p) => setRegisterAi(p)} />
            )}
          </>
        )}

      {/* Content */}
      {isListInitialLoading ? (
        <TableSkeleton
          rows={10}
          columns={(isAdmin ? 1 : 0) + (hipaaEnabled ? 10 : 9)}
          columnWidths={[
            ...(isAdmin ? [20] : []),
            52,
            '36%',
            88,
            112,
            92,
            90,
            112,
            ...(hipaaEnabled ? [54] : []),
            96,
            52,
          ]}
        />
      ) : isEmpty ? (
        <PageEmptyState
          icon={Boxes}
          title='No assets in inventory yet'
          description="Add your critical infrastructure, databases, repositories, and SaaS tools. Classify what's in scope for each framework, then start linking risks and controls."
          actions={
            canWorkspaceWrite ? (
              <Button
                size='sm'
                className={pageEmptyStatePrimaryButtonClass}
                onClick={() => setShowAddForm(true)}
              >
                <Plus className='h-3.5 w-3.5' />
                Add First Asset
              </Button>
            ) : null
          }
        />
      ) : totalItems === 0 ? (
        <TableEmptyState
          message='No assets match the selected filters.'
          className='min-h-[307.5px]'
        />
      ) : (
        <div className='flex min-h-0 items-start gap-0'>
          {/* Asset table */}
          <div className='w-full overflow-hidden rounded-sm border border-border bg-card transition-all'>
            <div className='overflow-x-auto overflow-y-visible'>
              <table className='w-full'>
                <thead className='sticky top-0 z-10'>
                  <tr
                    className={`h-[40px] border-b border-border ${hasSelection ? 'bg-selected' : 'bg-surface-subtle'}`}
                  >
                    {isAdmin && (
                      <th className='w-[36px] px-3'>
                        <Checkbox
                          checked={
                            pageItems.length > 0 &&
                            pageItems.every((i: any) => selectedIds.has(i.id))
                          }
                          onCheckedChange={toggleSelectAll}
                          className='h-3.5 w-3.5 rounded-[3px]'
                        />
                      </th>
                    )}
                    <th className='font-body w-[72px] px-3 text-left text-[10px] font-semibold tracking-[1px] text-text-subtle uppercase'>
                      {hasSelection ? (
                        <span className='flex items-center gap-2 tracking-normal whitespace-nowrap normal-case'>
                          <span className='font-body text-[11px] font-semibold text-cobalt-strong'>
                            {selectedIds.size} selected
                            {bulkUpdateMutation.isPending && '...'}
                          </span>
                          <button
                            onClick={() => setSelectedIds(new Set())}
                            className='font-body text-[10px] text-text-subtle hover:text-text-body'
                          >
                            Clear
                          </button>
                          <AssignAccessBulkButton
                            assetIds={[...selectedIds]}
                            onDone={() => setSelectedIds(new Set())}
                          />
                        </span>
                      ) : (
                        'ID'
                      )}
                    </th>
                    <th className='font-body px-3 text-left text-[10px] font-semibold tracking-[1px] text-text-subtle uppercase'>
                      {hasSelection ? '' : 'Name'}
                    </th>
                    <th className='w-[110px] px-3 text-left align-middle'>
                      {hasSelection ? (
                        <Select
                          onValueChange={(v) =>
                            handleBulkUpdate({ assetType: v })
                          }
                        >
                          <SelectTrigger className='h-6 w-full rounded-sm border-cobalt-border bg-card text-[10px]'>
                            <SelectValue placeholder='Type' />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(ASSET_TYPE_LABELS).map(([k, v]) => (
                              <SelectItem key={k} value={k}>
                                {v}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className='font-body text-[10px] font-semibold tracking-[1px] text-text-subtle uppercase'>
                          Type
                        </span>
                      )}
                    </th>
                    <th className='w-[110px] px-3 text-left align-middle'>
                      {hasSelection ? (
                        <Select
                          onValueChange={(v) =>
                            handleBulkUpdate({ dataClassification: v })
                          }
                        >
                          <SelectTrigger className='h-6 w-full rounded-sm border-cobalt-border bg-card text-[10px]'>
                            <SelectValue placeholder='Classification' />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(CLASSIFICATION_LABELS).map(
                              ([k, v]) => (
                                <SelectItem key={k} value={k}>
                                  {v}
                                </SelectItem>
                              )
                            )}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className='font-body text-[10px] font-semibold tracking-[1px] text-text-subtle uppercase'>
                          Classification
                        </span>
                      )}
                    </th>
                    <th className='w-[90px] px-3 text-left align-middle'>
                      {hasSelection ? (
                        <Select
                          onValueChange={(v) =>
                            handleBulkUpdate({ criticality: v })
                          }
                        >
                          <SelectTrigger className='h-6 w-full rounded-sm border-cobalt-border bg-card text-[10px]'>
                            <SelectValue placeholder='Criticality' />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(CRITICALITY_LABELS).map(([k, v]) => (
                              <SelectItem key={k} value={k}>
                                {v}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className='font-body text-[10px] font-semibold tracking-[1px] text-text-subtle uppercase'>
                          Criticality
                        </span>
                      )}
                    </th>
                    <th className='w-[100px] px-3 text-left align-middle'>
                      {hasSelection ? (
                        <Select
                          onValueChange={(v) =>
                            handleBulkUpdate({
                              environment: v === '_none' ? null : v,
                            })
                          }
                        >
                          <SelectTrigger className='h-6 w-full rounded-sm border-cobalt-border bg-card text-[10px]'>
                            <SelectValue placeholder='Environment' />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value='_none'>Not set</SelectItem>
                            <SelectItem value='production'>
                              Production
                            </SelectItem>
                            <SelectItem value='staging'>Staging</SelectItem>
                            <SelectItem value='development'>
                              Development
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className='font-body text-[10px] font-semibold tracking-[1px] text-text-subtle uppercase'>
                          Environment
                        </span>
                      )}
                    </th>
                    <th className='w-[110px] px-3 text-left align-middle'>
                      {hasSelection ? (
                        <Select
                          value=''
                          onValueChange={(action) =>
                            setScopeDialog({
                              ids: [...selectedIds],
                              action: action as Exclude<ScopeFilter, 'all'>,
                            })
                          }
                        >
                          <SelectTrigger
                            aria-label='Bulk scope'
                            className='h-6 w-full rounded-sm border-cobalt-border bg-card text-[10px]'
                          >
                            <SelectValue placeholder='Scope' />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value='out_of_scope'>
                              Mark out of scope
                            </SelectItem>
                            <SelectItem value='in_scope'>
                              Return to scope
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className='font-body text-[10px] font-semibold tracking-[1px] text-text-subtle uppercase'>
                          Scope
                        </span>
                      )}
                    </th>
                    <th className='w-[130px] px-3 text-left align-middle'>
                      {hasSelection ? (
                        <Select
                          onValueChange={(v) =>
                            handleBulkUpdate({
                              ownerId: v === '_unassigned' ? null : v,
                            })
                          }
                        >
                          <SelectTrigger className='h-6 w-full rounded-sm border-cobalt-border bg-card text-[10px]'>
                            <SelectValue placeholder='Owner' />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value='_unassigned'>
                              Unassigned
                            </SelectItem>
                            {(rolesQuery.data ?? []).map((role: any) => {
                              const personNames = (role.holders ?? [])
                                .map((holder: any) => holder.name || holder.email)
                                .filter(Boolean)
                                .join(', ')
                              return (
                                <SelectItem key={role.id} value={role.id}>
                                  {role.name}
                                  {personNames ? ` (${personNames})` : ''}
                                </SelectItem>
                              )
                            })}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className='font-body text-[10px] font-semibold tracking-[1px] text-text-subtle uppercase'>
                          Owner
                        </span>
                      )}
                    </th>
                    {hipaaEnabled && (
                      <th className='w-[70px] px-3 text-left align-middle'>
                        {hasSelection ? (
                          <Select
                            onValueChange={(v) =>
                              handleBulkUpdate({ containsEphi: v === 'yes' })
                            }
                          >
                            <SelectTrigger className='h-6 w-full rounded-sm border-cobalt-border bg-card text-[10px]'>
                              <SelectValue placeholder='ePHI' />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value='yes'>Contains ePHI</SelectItem>
                              <SelectItem value='no'>PHI-free</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className='font-body text-[10px] font-semibold tracking-[1px] text-text-subtle uppercase'>
                            ePHI
                          </span>
                        )}
                      </th>
                    )}
                    <th className='font-body w-[110px] px-3 text-left text-[10px] font-semibold tracking-[1px] text-text-subtle uppercase'>
                      {hasSelection ? '' : 'Last Review'}
                    </th>
                    <th className='font-body w-[72px] px-3 text-center text-[10px] font-semibold tracking-[1px] text-text-subtle uppercase'>
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pageItems.map((item: any) => {
                    const isStale =
                      !item.lastReviewedAt ||
                      new Date(item.lastReviewedAt) <
                        new Date(Date.now() - 365 * 24 * 60 * 60 * 1000)
                    return (
                      <tr
                        key={item.id}
                        className={`cursor-pointer border-b border-border transition-colors last:border-b-0 ${
                          hasSelection && selectedIds.has(item.id)
                            ? 'bg-selected'
                            : 'hover:bg-surface-subtle'
                        }`}
                        onClick={() => openAssetDetail(item.id)}
                      >
                        {isAdmin && (
                          <td
                            className='px-3 py-2.5'
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Checkbox
                              checked={selectedIds.has(item.id)}
                              onCheckedChange={() => toggleSelect(item.id)}
                              className='h-3.5 w-3.5 rounded-[3px]'
                            />
                          </td>
                        )}
                        <td className='font-body px-3 py-2.5 text-[12px] font-medium whitespace-nowrap text-cobalt-strong'>
                          {item.displayId}
                        </td>
                        <td className='px-3 py-2.5'>
                          <div className='flex items-center gap-1.5'>
                            <span className='font-body line-clamp-1 text-[12px] font-medium text-foreground'>
                              {item.name}
                            </span>
                            {isSyncedAsset(item) && (
                              <span className='font-body inline-flex h-[16px] shrink-0 items-center rounded-sm bg-primary-subtle px-1.5 text-[9px] font-medium tracking-[0.5px] text-cobalt-strong uppercase'>
                                {item.provider}
                              </span>
                            )}
                            {item.syncStatus === 'ORPHANED' && (
                              <span className='font-body inline-flex h-[16px] shrink-0 items-center rounded-sm bg-status-orange-bg px-1.5 text-[9px] font-medium tracking-[0.5px] text-status-orange uppercase'>
                                orphaned
                              </span>
                            )}
                          </div>
                          {item.description && (
                            <div className='font-body mt-0.5 line-clamp-1 text-[10px] text-text-placeholder'>
                              {item.description}
                            </div>
                          )}
                          {item.classificationSuggestion && (
                            <ClassificationSuggestionCard
                              assetId={item.id}
                              suggestion={item.classificationSuggestion}
                              onMutated={invalidateAll}
                              readOnly={!canWorkspaceWrite}
                            />
                          )}
                        </td>
                        <td className='px-3 py-2.5'>
                          <div className='font-body text-[9px] font-semibold tracking-[0.5px] text-text-placeholder uppercase'>
                            {ASSET_CATEGORY_LABELS[
                              ASSET_CATEGORY_FOR_TYPE[
                                item.assetType as keyof typeof ASSET_CATEGORY_FOR_TYPE
                              ]
                            ] ?? ''}
                          </div>
                          <span className='font-body text-[10px] font-semibold tracking-[0.5px] text-muted-foreground uppercase'>
                            {ASSET_TYPE_LABELS[item.assetType] ??
                              item.assetType}
                          </span>
                        </td>
                        <td className='px-3 py-2.5'>
                          <span
                            className={`font-body inline-flex h-[20px] items-center rounded-md px-2 text-[10px] font-semibold tracking-[0.5px] uppercase ${classificationColor(item.dataClassification)}`}
                          >
                            {CLASSIFICATION_LABELS[item.dataClassification] ??
                              item.dataClassification}
                          </span>
                        </td>
                        <td className='px-3 py-2.5'>
                          <span
                            className={`font-body inline-flex h-[20px] items-center rounded-md px-2 text-[10px] font-semibold tracking-[0.5px] uppercase ${criticalityColor(item.criticality)}`}
                          >
                            {CRITICALITY_LABELS[item.criticality] ??
                              item.criticality}
                          </span>
                        </td>
                        <td className='font-body px-3 py-2.5 text-[11px] text-muted-foreground capitalize'>
                          {item.environment ?? '—'}
                        </td>
                        <td className='px-3 py-2.5'>
                          {item.outOfScope ? (
                            <span
                              title='An active scope exception suppresses every check for this resource'
                              className='font-body bg-status-orange-bg text-status-orange inline-flex h-[20px] items-center rounded-md px-2 text-[10px] font-semibold tracking-[0.5px] whitespace-nowrap uppercase'
                            >
                              Out of scope
                            </span>
                          ) : (
                            <span className='font-body text-text-subtle text-[11px]'>
                              In scope
                            </span>
                          )}
                        </td>
                        <td className='font-body px-3 py-2.5 text-[11px] text-text-body'>
                          {item.ownerRoleName ?? item.ownerLabel}
                        </td>
                        {hipaaEnabled && (
                          <td className='px-3 py-2.5 text-center'>
                            {item.containsEphi ? (
                              <span className='font-body text-[10px] font-semibold tracking-[0.5px] text-status-red uppercase'>
                                Yes
                              </span>
                            ) : (
                              <span className='font-body text-[10px] text-text-placeholder'>
                                —
                              </span>
                            )}
                          </td>
                        )}
                        <td
                          title={
                            isStale
                              ? item.lastReviewedAt
                                ? 'Review overdue: last reviewed over a year ago'
                                : 'Never reviewed'
                              : undefined
                          }
                          className={`font-body px-3 py-2.5 text-[11px] whitespace-nowrap ${isStale ? 'font-semibold text-status-orange' : 'text-muted-foreground'}`}
                        >
                          {isStale && (
                            <AlertTriangle className='mr-1 inline h-3 w-3' />
                          )}
                          {formatDate(item.lastReviewedAt)}
                        </td>
                        <td className='px-3 py-2.5 text-center'>
                          <button
                            type='button'
                            aria-label={`View ${item.displayId} detail`}
                            className='inline-flex h-7 w-7 items-center justify-center rounded-sm text-text-subtle transition-colors hover:bg-muted hover:text-foreground'
                            onClick={(event) => {
                              event.stopPropagation()
                              openAssetDetail(item.id)
                            }}
                          >
                            <Eye className='h-3.5 w-3.5' />
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <TablePaginationFooter
              page={page}
              pageSize={pageSize}
              totalItems={totalItems}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          </div>
        </div>
      )}

      {showAddForm && canWorkspaceWrite && (
        <AddAssetDialog
          open={showAddForm}
          onClose={() => setShowAddForm(false)}
          onCreated={invalidateAll}
          readOnly={!canWorkspaceWrite}
        />
      )}
      {registerAi && canWorkspaceWrite && aiGovernanceEnabled && (
        <RegisterAiSystemDialog
          open
          onClose={() => setRegisterAi(null)}
          onCreated={invalidateAll}
          prefill={registerAi}
        />
      )}
      {scopeDialog && isAdmin && (
        <AssetScopeDialog
          open
          onOpenChange={(open) => {
            if (!open) setScopeDialog(null)
          }}
          assetIds={scopeDialog.ids}
          action={scopeDialog.action}
          onMutated={() => {
            void queryClient.invalidateQueries({
              queryKey: trpc.assetManagement.pathKey(),
            })
          }}
        />
      )}
      <AssetSyncDialog
        open={showSyncDialog && canWorkspaceWrite}
        onOpenChange={setShowSyncDialog}
        providerGroups={providerGroups}
        selectedTargetKeys={selectedSyncTargets}
        syncing={isSyncingSources}
        onToggleProvider={handleToggleSyncProvider}
        onToggleTarget={handleToggleSyncTarget}
        onSync={handleSyncSelectedSources}
        readOnly={!canWorkspaceWrite}
        canManageEnvironments={isAdmin}
      />
    </div>
  )
}
